import { useContext } from 'react';

import { Node } from 'reactflow';

import { AuthContext } from '../contexts/AuthContext';

export type NodeMetadata = {
    created_at: number;
    updated_at: number;
    user_uuid: string | null;
};

export const useNodeMetadata = () => {
    const { user } = useContext(AuthContext);

    /**
     * Add metadata to a new node
     * @param node The node to add metadata to
     * @returns The node with metadata added
     */
    const addMetadataToNode = (node: Node): Node => {
        const now = Math.floor(Date.now() / 1000);
        return {
            ...node,
            data: {
                ...node.data,
                metadata: {
                    created_at: now,
                    updated_at: now,
                    user_uuid: user?.uuid || null,
                } as NodeMetadata,
            },
        };
    };

    /**
     * Update metadata on an existing node
     * @param node The node to update metadata on
     * @returns The node with updated metadata
     */
    const updateNodeMetadata = (node: Node): Node => {
        const now = Math.floor(Date.now() / 1000);
        return {
            ...node,
            data: {
                ...node.data,
                metadata: {
                    ...node.data?.metadata,
                    updated_at: now,
                    user_uuid: user?.uuid || null,
                } as NodeMetadata,
            },
        };
    };

    /**
     * Get the metadata from a node
     * @param node The node to get metadata from
     * @returns The node's metadata or null if not present
     */
    const getNodeMetadata = (node: Node): NodeMetadata | null => {
        return (node.data?.metadata as NodeMetadata) || null;
    };

    return {
        addMetadataToNode,
        updateNodeMetadata,
        getNodeMetadata,
    };
};
